/**
 * Approve Job Dialog
 * Confirmation dialog for transitioning a job from pending_review to completed.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useSession } from '@/hooks/useSession';
import { useUpdateInstallationJob } from '../hooks/useInstallationJobs';
import type { InstallationJob } from '../types';

interface ApproveJobDialogProps {
  job: InstallationJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApproveJobDialog({ job, open, onOpenChange }: ApproveJobDialogProps) {
  const { user } = useSession();
  const updateJob = useUpdateInstallationJob();
  const [reviewNotes, setReviewNotes] = useState('');

  const reviewerName = user?.name || [user?.firstname, user?.lastname].filter(Boolean).join(' ') || 'Unknown';

  const handleApprove = async () => {
    try {
      const now = new Date().toISOString();

      // Mark all sensors as installed
      const sensors = job.sensors.map((s) =>
        s.installed ? s : { ...s, installed: true, installedAt: now },
      );

      // Mark all transmitters as installed
      const transmitters = job.transmitters.map((t) =>
        t.installed ? t : { ...t, installed: true, installedAt: now },
      );

      await updateJob.mutateAsync({
        id: job.id,
        input: {
          status: 'completed',
          completedAt: now,
          completedByName: reviewerName,
          reviewNotes: reviewNotes.trim() || undefined,
          sensors,
          transmitters,
        },
      });
      toast.success('Job approved and completed');
      setReviewNotes('');
      onOpenChange(false);
    } catch {
      toast.error('Failed to approve job');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Approve & Complete Job
          </DialogTitle>
          <DialogDescription>
            Mark <strong>{job.jobNumber}</strong> — {job.title} as completed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="text-sm text-muted-foreground">
            Reviewed by: <strong>{reviewerName}</strong>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-notes">Review Notes (optional)</Label>
            <textarea
              id="review-notes"
              placeholder="Any notes about this review..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            disabled={updateJob.isPending}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {updateJob.isPending ? 'Approving...' : 'Approve & Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
